import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapJurisdictionsResponseToDto, mapKycSettingsResponseToDto } from '../dto/system-kyc.dto.js';
import { reorderKycRows } from '../model/system-kyc.model.js';

export function useSystemKyc() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryJurisdictionId = String(searchParams.get('jurisdictionId') || '').trim();

  const [rows, setRows] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState(queryJurisdictionId);
  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [dragSourceId, setDragSourceId] = useState('');

  const detailsQuery = useMemo(
    () => (selectedJurisdictionId ? `?jurisdictionId=${encodeURIComponent(selectedJurisdictionId)}` : ''),
    [selectedJurisdictionId],
  );

  useEffect(() => {
    setSelectedJurisdictionId(queryJurisdictionId);
  }, [queryJurisdictionId]);

  const updateJurisdictionQuery = useCallback((jurisdictionId) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextValue = String(jurisdictionId || '').trim();

    if (nextValue) {
      nextParams.set('jurisdictionId', nextValue);
    } else {
      nextParams.delete('jurisdictionId');
    }

    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadJurisdictions = useCallback(async () => {
    setIsJurisdictionsLoading(true);

    try {
      const response = await adminApi.getJurisdictions(token);
      const list = mapJurisdictionsResponseToDto(response);
      setJurisdictions(list);

      if (list.length === 0) {
        setSelectedJurisdictionId('');
        updateJurisdictionQuery('');
        return;
      }

      const hasSelected = list.some((item) => item.id === queryJurisdictionId);
      if (hasSelected) {
        return;
      }

      const fallbackId = list[0].id;
      setSelectedJurisdictionId(fallbackId);
      updateJurisdictionQuery(fallbackId);
    } catch (requestError) {
      if (requestError?.status === 404) {
        setJurisdictions([]);
        setSelectedJurisdictionId('');
        updateJurisdictionQuery('');
      } else {
        setJurisdictions([]);
        setSelectedJurisdictionId('');
        setError(requestError.message || 'Failed to load jurisdictions');
      }
    } finally {
      setIsJurisdictionsLoading(false);
    }
  }, [queryJurisdictionId, token, updateJurisdictionQuery]);

  const loadSteps = useCallback(async () => {
    if (isJurisdictionsLoading) {
      return;
    }

    const isKnownJurisdiction = jurisdictions.some((item) => item.id === selectedJurisdictionId);
    if (!selectedJurisdictionId || !isKnownJurisdiction) {
      setRows([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getKycSettings(token, selectedJurisdictionId);
      setRows(mapKycSettingsResponseToDto(response));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setError(requestError.message || 'Failed to load KYC steps');
        setRows([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isJurisdictionsLoading, jurisdictions, selectedJurisdictionId, token]);

  useEffect(() => {
    loadJurisdictions();
  }, [loadJurisdictions]);

  useEffect(() => {
    loadSteps();
  }, [loadSteps]);

  const persistOrder = useCallback(
    async (nextRows, successMessage) => {
      if (!selectedJurisdictionId) {
        return;
      }

      setIsSaving(true);
      setError('');
      setSuccess('');

      try {
        for (let index = 0; index < nextRows.length; index += 1) {
          const row = nextRows[index];
          await adminApi.updateKycStep(token, selectedJurisdictionId, row.id, {
            name: String(row.name || '').trim(),
            description: String(row.description || '').trim(),
            position: index,
          });
        }

        await loadSteps();
        setSuccess(successMessage || 'Saved successfully.');
      } catch (requestError) {
        setError(requestError.message || 'Failed to save order');
        await loadSteps();
      } finally {
        setIsSaving(false);
      }
    },
    [loadSteps, selectedJurisdictionId, token],
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteTargetId || !selectedJurisdictionId) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.deleteKycStep(token, selectedJurisdictionId, deleteTargetId);
      setRows((prev) => prev.filter((row) => row.id !== deleteTargetId).map((row, index) => ({ ...row, order: index })));
      setDeleteTargetId('');
      setSuccess('Step deleted.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to delete step');
    } finally {
      setIsSaving(false);
    }
  }, [deleteTargetId, selectedJurisdictionId, token]);

  const handleDrop = useCallback(async (targetId) => {
    if (!dragSourceId || dragSourceId === targetId || isSaving || !selectedJurisdictionId) {
      setDragSourceId('');
      return;
    }

    const nextRows = reorderKycRows(rows, dragSourceId, targetId);
    setRows(nextRows);
    setDragSourceId('');
    await persistOrder(nextRows, 'Order updated.');
  }, [dragSourceId, isSaving, persistOrder, rows, selectedJurisdictionId]);

  const toggleStepImmutable = useCallback(async (row) => {
    if (!row?.id || isSaving || !selectedJurisdictionId) {
      return;
    }

    const nextImmutable = !Boolean(row.immutable);
    setRows((prev) => prev.map((item) => (
      item.id === row.id ? { ...item, immutable: nextImmutable } : item
    )));

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.updateKycStep(token, selectedJurisdictionId, row.id, {
        name: String(row.name || '').trim(),
        description: String(row.description || '').trim(),
        position: Number.isFinite(row.order) ? row.order : 0,
        immutable: nextImmutable,
      });
      setSuccess('Step updated.');
    } catch (requestError) {
      setRows((prev) => prev.map((item) => (
        item.id === row.id ? { ...item, immutable: Boolean(row.immutable) } : item
      )));
      setError(requestError.message || 'Failed to update step');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, selectedJurisdictionId, token]);

  return {
    rows,
    jurisdictions,
    selectedJurisdictionId,
    isJurisdictionsLoading,
    isLoading,
    isSaving,
    error,
    success,
    deleteTargetId,
    dragSourceId,
    detailsQuery,
    setDeleteTargetId,
    setDragSourceId,
    setSelectedJurisdictionId,
    updateJurisdictionQuery,
    confirmDelete,
    handleDrop,
    toggleStepImmutable,
  };
}

